export interface PatientAi {
  id?: string;
  first_name: string;
  last_name: string;
  last_summary_generation_date: string;
  last_summary_generation_time: string;
  generation_is_in_progress: boolean;
}

export interface SummaryAll {
  id?: string;
  last_generation_date: string;
  last_generation_time: string;
  html: string;
}

export interface PatDmrRelatedData {
  id?: string;
  last_generation_date: string;
  last_generation_time: string;
  html: string;
}

export interface LastAppointmentSummary {
  id?: string;
  title: string;
  date: string;
  generative_summary: string;
}

export interface NuclearTopicsDistrEnriched {
  date: string;
  title: string;
  categories_distribution_available: boolean;
  categories_distribution: NuclearTopicsDistr | null;
}

export type NuclearTopicsDistr = {
  [key in
    | 'SPS'
    | 'FAM'
    | 'RA'
    | 'OS'
    | 'RS'
    | 'MDE'
    | 'PRG'
    | 'ET'
    | 'RCT'
    | 'SES'
    | 'BNS'
    | 'SV']: number;
};

export interface Topic {
  name: string;
  words: [];
}

export interface AdditionalTopicScores {
  id: string;
  name: string;
  score: number;
}

export interface CategoryWithVisibility {
  name: string;
  hidden: boolean;
}
export interface TextAndSize {
  text: string;
  size: number;
}

export interface UploadCompleteResponse {
  appointment_id: string;
  ai_pipeline_state: AIAppointmentPipelineStatus;
}
export interface AppointmentAiState {
  ai_pipeline_state: AIAppointmentPipelineStatus; // this is actually not used
  transcription_state: string; // this is actually not used
  appsumm_state: string;
  appwordcloud_state: string;
  apptopics_state: string;
  catdistr_state: string;
}

export enum AIAppointmentPipelineStatus {
  NOT_READY_TO_START = 'NOT_READY_TO_START',
  READY_TO_START = 'READY_TO_START',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESSFULLY_EXECUTED = 'SUCCESSFULLY_EXECUTED',
  FAILED = 'FAILED',
}

export interface WordCloudData {
  word: string;
  score: number;
}

export interface GraphTopic {
  id: string;
  topic: string;
  description: string;
}

export const NUCLEAR_TOPICS_EXPLANATION: GraphTopic[] = [
  {
    id: 'SPS',
    topic: 'Sofferenza/Patologia/Sintomi',
    description:
      'Questa categoria raccoglie tutte le informazioni che riguardano il malessere emotivo, psicologico o fisico del paziente. Include riferimenti alla sofferenza personale, alla sintomatologia psicopatologica o ad aspetti di malessere esistenziale passati e presenti. Si riferisce sia a problematiche specifiche (ad esempio, ansia, depressione, attacchi di panico) sia a manifestazioni generali di malessere che influenzano la vita quotidiana del paziente.',
  },
  {
    id: 'FAM',
    topic: 'Famiglia',
    description:
      'Questa categoria riguarda tutte le informazioni e i significati relativi alla famiglia del paziente, comprese le relazioni con i familiari. Include i riferimenti ai rapporti con genitori, fratelli, sorelle, e in caso di matrimonio, anche con il/la coniuge e i figli. Vengono considerati sia gli aspetti relazionali, emotivi e sentimentali che il paziente sperimenta nei confronti dei familiari, sia le caratteristiche e i comportamenti dei membri della famiglia.',
  },
  {
    id: 'RA',
    topic: 'Relazione Affettiva',
    description:
      'Questa categoria comprende tutte le informazioni e i significati relativi ai rapporti affettivi romantici, passati e presenti, del paziente. Include la descrizione di sentimenti, emozioni e relazioni con partner romantici, nonché i modi di essere di questi ultimi. Riguarda sia le relazioni in corso che quelle passate, nonché la percezione che il paziente ha di esse.',
  },
  {
    id: 'OS',
    topic: 'Occupazione/Studio',
    description:
      "Questa categoria include le informazioni riguardanti l'occupazione lavorativa del paziente o il suo percorso di studio. Comprende anche le relazioni con colleghi, superiori o insegnanti, e le eventuali difficoltà o soddisfazioni legate al lavoro o allo studio. Riguarda i modi in cui il paziente si relaziona al proprio ruolo lavorativo o scolastico e le implicazioni emotive connesse.",
  },
  {
    id: 'RS',
    topic: 'Rete Sociale',
    description:
      'Questa categoria include le informazioni e i significati relativi alla rete sociale del paziente, incluse amicizie, conoscenti e altri rapporti sociali significativi. Si riferisce alle dinamiche relazionali al di fuori del contesto familiare e affettivo-romantico, esplorando il modo in cui il paziente si rapporta agli altri, le emozioni e i sentimenti che vive nelle relazioni amicali e sociali, e le eventuali difficoltà o soddisfazioni in questo ambito.',
  },
  {
    id: 'MDE',
    topic: 'Modi Di Essere',
    description:
      'Questa categoria riguarda i tratti di personalità, il carattere, il temperamento e le abitudini del paziente che definiscono il suo modo di percepire e interagire con il mondo. Include le riflessioni del paziente sul proprio sé, la sua autostima, il senso di corporeità, il rapporto con il proprio corpo. Riguarda anche la descrizione di emozioni ricorrenti, tendenze emotive e comportamentali che influenzano la sua vita quotidiana.',
  },
  {
    id: 'PRG',
    topic: 'Progettualità',
    description:
      'Questa categoria comprende tutte le informazioni relative ai desideri, progetti e aspettative future del paziente. Include riflessioni su obiettivi personali, professionali, familiari o sociali che il paziente desidera raggiungere. Può riguardare sia pianificazioni concrete che desideri più astratti o ideali, e include anche la percezione del paziente rispetto ai propri progressi o ostacoli nel realizzare tali progetti.',
  },
  {
    id: 'ET',
    topic: 'Esperienza Terapeutica',
    description:
      "Questa categoria si concentra su come il paziente vive la relazione terapeutica e il processo della terapia. Include il modo in cui il paziente percepisce l'alleanza terapeutica, la fiducia, la comunicazione e le proprie emozioni in terapia. Comprende anche i riferimenti a eventuali compiti o esercizi assegnati dal terapeuta e come il paziente li ha sperimentati. Inoltre, rientrano in questa categoria i feedback del paziente sull’efficacia o i progressi della terapia e riflessioni sul proprio coinvolgimento nel percorso terapeutico.",
  },
  {
    id: 'RT',
    topic: 'Rifigurazioni Terapeutiche',
    description:
      'Questa categoria si concentra sugli interventi terapeutici, quali rifigurazioni, interpretazioni e riformulazioni proposte dal terapeuta durante le sedute. Include le volte in cui il terapeuta aiuta il paziente a rivedere, ristrutturare o comprendere meglio eventi, emozioni o pensieri. Rientrano in questa categoria i momenti in cui il terapeuta introduce nuove prospettive o significati che portano il paziente a riconsiderare la propria esperienza e quando assegna compiti terapeutici.',
  },
  {
    id: 'SES',
    topic: 'Sessualità',
    description:
      "Questa categoria include tutte le informazioni e i significati relativi alla sessualità del paziente e al suo orientamento sessuale. Comprende la sfera intima, le esperienze sessuali, i desideri, le fantasie, le preferenze sessuali, le eventuali problematiche o difficoltà sessuali e il rapporto con il proprio corpo e con l'intimità. Rientrano in questa categoria anche i pensieri e le emozioni che il paziente esperisce rispetto alla propria vita sessuale, oltre a eventuali aspetti legati all’identità sessuale o all'esplorazione del sé attraverso la sessualità.",
  },
  {
    id: 'BNS',
    topic: 'Benessere',
    description:
      "Questa categoria raccoglie informazioni e significati legati a esperienze di benessere, soddisfazione, equilibrio emotivo e serenità del paziente. Include riflessioni su stati d'animo positivi, emozioni gratificanti, momenti di soddisfazione nella vita quotidiana e la percezione di uno stato di benessere generale. Può riferirsi anche a pensieri e comportamenti che promuovono uno stile di vita sano e un atteggiamento ottimista verso la vita e il futuro. Include il modo in cui il paziente vive esperienze di appagamento o serenità in vari ambiti della sua vita.",
  },
  {
    id: 'SV',
    topic: 'Sistema Valoriale',
    description:
      'Questa categoria riguarda tutte le informazioni e i significati relativi ai valori, principi e credenze che guidano il comportamento e le scelte del paziente. Include i valori morali, etici, religiosi, culturali, familiari e personali. Può riferirsi a come il paziente interpreta e vive le proprie convinzioni, nonché alle tradizioni culturali e religiose che influenzano la sua visione del mondo. Rientrano in questa categoria anche i conflitti interni o esterni legati a valori o credenze, così come la riflessione del paziente su questioni morali o esistenziali.',
  },
];
